const encoder = new TextEncoder()
const decoder = new TextDecoder('utf-8', { fatal: true, ignoreBOM: false })

/** Growable byte buffer with LEB128 unsigned varints. Safe up to Number.MAX_SAFE_INTEGER. */
export class ByteWriter {
  private buf = new Uint8Array(256)
  private len = 0

  private ensure(extra: number): void {
    if (this.len + extra <= this.buf.length) return
    const next = new Uint8Array(Math.max(this.buf.length * 2, this.len + extra))
    next.set(this.buf.subarray(0, this.len))
    this.buf = next
  }

  uint(value: number): this {
    if (!Number.isInteger(value) || value < 0 || value > Number.MAX_SAFE_INTEGER) {
      throw new RangeError(`cannot encode ${value} as an unsigned varint`)
    }
    this.ensure(10)
    let v = value
    while (v >= 0x80) {
      this.buf[this.len++] = (v % 0x80) | 0x80
      v = Math.floor(v / 0x80)
    }
    this.buf[this.len++] = v
    return this
  }

  raw(bytes: Uint8Array): this {
    this.ensure(bytes.length)
    this.buf.set(bytes, this.len)
    this.len += bytes.length
    return this
  }

  string(text: string): this {
    const bytes = encoder.encode(text)
    return this.uint(bytes.length).raw(bytes)
  }

  bytes(): Uint8Array {
    return this.buf.slice(0, this.len)
  }
}

export class ByteReader {
  private pos = 0

  constructor(private readonly buf: Uint8Array) {}

  uint(): number {
    let result = 0
    let scale = 1
    for (;;) {
      const byte = this.buf[this.pos]
      if (byte === undefined) throw new RangeError('truncated varint')
      this.pos++
      result += (byte & 0x7f) * scale
      if (byte < 0x80) return result
      scale *= 0x80
      if (scale > Number.MAX_SAFE_INTEGER) throw new RangeError('varint too large')
    }
  }

  raw(length: number): Uint8Array {
    if (this.pos + length > this.buf.length) throw new RangeError('truncated bytes')
    const out = this.buf.slice(this.pos, this.pos + length)
    this.pos += length
    return out
  }

  string(): string {
    return decoder.decode(this.raw(this.uint()))
  }

  done(): boolean {
    return this.pos >= this.buf.length
  }
}
